/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box } from '@apia/theme';
import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useIconsListStoreContext } from './store/context';
import { isChild, TId, noNaN } from '@apia/util';
import { TIcon, TIconsList } from './types';
import { ApiaUtil } from '../../objects/ApiaUtil';

function findIcon(
  icons: TIcon[],
  arg: EventTarget | TId,
): TIcon | null | undefined {
  const iconId =
    arg instanceof HTMLElement
      ? arg.closest<HTMLElement>('.iconsList__iconWrapper[data-id]')?.dataset
          ?.id
      : arg;

  if (iconId !== undefined) {
    const icon = icons.find((current) => String(current.id) === iconId);

    return icon ?? null;
  }
  return null;
}

export const KeyHandler = ({
  children,
  icons,
  onIconClick,
  onIconDoubleClick,
  onIconGetDetails,
  onIconKeyDown,
  storeId,
}: { children: ReactNode; storeId: TId } & Pick<
  TIconsList<any>,
  | 'icons'
  | 'onIconClick'
  | 'onIconDoubleClick'
  | 'onIconGetDetails'
  | 'onIconKeyDown'
>) => {
  const { actions, getState } = useIconsListStoreContext();
  const timeoutId = useRef(-1);
  const currentIcon = useRef<TIcon | null | undefined>(null);

  const showIconDetails = useCallback(
    (icon: TIcon) => {
      if (onIconGetDetails) {
        ApiaUtil.instance.tooltips.open({
          attachToElement: () =>
            document.querySelector(
              `#${storeId} .iconsList__iconWrapper[data-id="${icon.id}"]`,
            ) as HTMLElement,
          children: onIconGetDetails(icon),
          closeOnClick: false,
          minSize: { width: 200, height: 0 },
        });
      }
    },
    [onIconGetDetails, storeId],
  );

  const handleMouseEnter = useCallback(
    (ev: MouseEvent<HTMLElement>) => {
      const icon = findIcon(icons, ev.target);

      if (icon !== currentIcon.current || icon === null || icon === undefined) {
        if (timeoutId.current !== -1) {
          clearTimeout(timeoutId.current);
        }

        timeoutId.current = window.setTimeout(() => {
          if (icon) {
            showIconDetails(icon);
            if (onIconClick && icon) {
              onIconClick(ev, icon);
            }
          }
        }, 100);

        currentIcon.current = icon;
      }
    },
    [icons, onIconClick, showIconDetails],
  );

  return (
    <Box
      className="iconsList__keyHandler"
      tabIndex={0}
      onFocus={useCallback(
        (ev: FocusEvent) => {
          if (
            !isChild(ev.target as HTMLElement, (current) =>
              current.classList?.contains('iconsList__keyHandler'),
            )
          )
            return;
          const icon = findIcon(icons, getState().focusedId);
          if (icon) {
            showIconDetails(icon);
          }
        },
        [getState, icons, showIconDetails],
      )}
      onMouseOver={handleMouseEnter}
      onMouseLeave={useCallback(() => {
        currentIcon.current = null;
      }, [])}
      onMouseDown={useCallback(
        (ev: MouseEvent) => {
          if (ev.target instanceof HTMLElement) {
            const colIndex = noNaN(
              ev.target.closest('[aria-colindex]')?.ariaColIndex,
              -1,
            );
            const rowIndex = noNaN(
              ev.target.closest('[aria-rowindex]')?.ariaRowIndex,
              -1,
            );

            if (colIndex >= 0 && rowIndex >= 0) {
              actions.setFocused({
                colIndex,
                rowIndex,
              });
            }
          }
        },
        [actions],
      )}
      onClick={useCallback(
        (ev: MouseEvent<HTMLElement>) => {
          const icon = findIcon(icons, ev.target);

          if (onIconClick && icon) {
            onIconClick(ev, icon);
          }
        },
        [icons, onIconClick],
      )}
      onDoubleClick={useCallback(
        (ev: MouseEvent<HTMLElement>) => {
          if (onIconDoubleClick) {
            const icon = findIcon(icons, ev.target);
            if (icon) onIconDoubleClick(ev, icon);
          }
        },
        [icons, onIconDoubleClick],
      )}
      onKeyDown={useCallback(
        (ev: KeyboardEvent<HTMLElement>) => {
          function showIconByFocusChange() {
            const icon = findIcon(icons, getState().focusedId);

            if (icon) showIconDetails(icon);
            if (icon && onIconKeyDown) {
              onIconKeyDown(ev, icon);
            }
          }

          function shoutKeyDown() {
            const icon = findIcon(icons, getState().focusedId);
            if (icon && onIconKeyDown) {
              onIconKeyDown(ev, icon);
            }
          }

          switch (ev.code) {
            case 'ArrowUp':
              actions.arrowUp();
              showIconByFocusChange();
              break;
            case 'ArrowDown':
              actions.arrowDown();
              showIconByFocusChange();
              break;
            case 'ArrowRight':
              actions.arrowRight();
              showIconByFocusChange();
              break;
            case 'ArrowLeft':
              actions.arrowLeft();
              showIconByFocusChange();
              break;
            default:
              shoutKeyDown();
              break;
          }
        },
        [actions, getState, icons, onIconKeyDown, showIconDetails],
      )}
    >
      {children}
    </Box>
  );
};
